// cbta-datepicker.component.ts
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import moment from "moment";

@Component({
	selector: "app-cbta-datepicker",
	templateUrl: "./cbta-datepicker.component.html",
	styleUrls: ["./cbta-datepicker.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CbtaDatepickerComponent implements OnInit, OnChanges, AfterViewInit {
	@Input() placeHolder: string = "";
	@Input() inputDesign: "searchType" | "formType" = "searchType";
	@Input() label: string = "";
	@Input() isDisabled: boolean = false;
	@Input() readOnly = false;
	@Input() format: string = "MM DD, YYYY";
	@Input() defaultValue?: Date | string | null;
	@Input() minDate?: Date | string | null;

	// No generics on UntypedFormControl
	@Input() datePickerInput = new UntypedFormControl(null);

	@ViewChild("datePicker") datePicker?: ElementRef<HTMLInputElement>;

	@Output() selectedDateChange = new EventEmitter<string>();
	@Output() onDatePickerInit = new EventEmitter<ElementRef>();

	constructor(private cd: ChangeDetectorRef) {}

	ngOnInit(): void {
		this.syncDisabledState();
		this.applyInitialDate();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["isDisabled"]) this.syncDisabledState();
		if (changes["defaultValue"] && !changes["defaultValue"].firstChange) {
			this.applyInitialDate();
		}
		if (changes["readOnly"] && !changes["readOnly"].firstChange) {
			this.applyInitialDate();
		}
		if (changes["format"] && !changes["format"].firstChange) {
			this.updateDisplayDateFormat(this.datePickerInput.value);
		}
	}

	ngAfterViewInit(): void {
		this.onDatePickerInit.emit(this.datePicker as ElementRef);
		this.updateDisplayDateFormat(this.datePickerInput.value);
	}

	handleClose(raw: Date | string | null | undefined): void {
		if (this.readOnly) return;
		const next = this.toDate(raw);
		if (!next) return;
		this.datePickerInput.patchValue(next, { emitEvent: true });
		this.updateDisplayDateFormat(next);
		this.selectedDateChange.emit(moment(next).format("YYYY-MM-DD"));
	}

	// ---------- Helpers ----------
	private applyInitialDate(): void {
		const initial = this.toDate(this.defaultValue);
		this.datePickerInput.patchValue(initial ?? null, { emitEvent: false });
		this.updateDisplayDateFormat(this.datePickerInput.value);
	}

	private syncDisabledState(): void {
		if (this.isDisabled) this.datePickerInput.disable({ emitEvent: false });
		else this.datePickerInput.enable({ emitEvent: false });
	}

	private updateDisplayDateFormat(date: Date | null): void {
		const el = this.datePicker?.nativeElement;
		if (!el) return;
		el.value = date ? moment(date).format(this.format) : "";
		this.cd.detectChanges();
	}

	private toDate(v: Date | string | null | undefined): Date | null {
		if (!v) return null;
		if (v instanceof Date) return isNaN(+v) ? null : v;

		// accept saved (YYYY-MM-DD), API ISO (2027-04-30T00:00:00.000Z), and display (DD/MM/YY)
		const strict = moment(v, ["YYYY-MM-DD", moment.ISO_8601, this.format], true);
		if (strict.isValid()) return strict.toDate();

		const loose = moment(v);
		return loose.isValid() ? loose.toDate() : null;
	}

	// Template helpers
	get normalizedMin(): Date | null {
		return this.toDate(this.minDate);
	}
	get startAt(): Date | null {
		return this.toDate(this.defaultValue) ?? (this.datePickerInput.value as Date | null) ?? null;
	}
}
